import { useState, useEffect } from 'react';
import Head from 'next/head';
import Link from 'next/link';
import { Layout, SignupForm } from '../../../components';
import { DownloadIcon } from '../../../components/Svg';
import Router from 'next/router';
import facilityBotApi from '../../../services/facilityBot';
import dayjs from 'dayjs';
import { Input, List, Button, Spin, Typography, Select } from 'antd';
import { openNotification } from '../../../utils';
const { Search } = Input;
const { Paragraph } = Typography;
import {
	ChecklistsResponse,
	ChecklistResponse,
	ChecklistCategoriesResponse,
} from '../../../response_schemas/checklists';
import { ApiError } from '../../../response_schemas/apiErrors';
import {
	CHECKLIST_OVERVIEW,
	CHECKLIST_DEPARTMENTS,
	CHECKLIST_BUILD_STEPS,
	CHECKLIST_FAQS,
	CHECKLIST_BOTTOM_CTA,
} from '../../../data/checklistLibraryData';

export default function ChecklistLibrary({ initialChecklists, initialCategories, initialPageInfo }) {
	const [checklists, setChecklists] = useState(initialChecklists);
	const [checklistsPageInfo, setchecklistsPageInfo] = useState(initialPageInfo);
	const [categories, setCategories] = useState(initialCategories);
	const [filter, setFilter] = useState({
		q: '',
		category_id: null,
		data_sort: 'created_at',
		order_sort: 'desc',
	});

	const [showDetail, setShowDetail] = useState(false);
	const [detailItem, setDetailItem] = useState({});
	const [loading, setLoading] = useState(false);
	const [activeSection, setActiveSection] = useState(CHECKLIST_DEPARTMENTS[0].id);
	const [expandedDepts, setExpandedDepts] = useState({});
	const [openFaqIndex, setOpenFaqIndex] = useState(0);

	useEffect(() => {
		let checklist_id = Router.query.id;
		if (checklist_id && checklist_id.length) {
			setLoading(true);
			setShowDetail(true);

			facilityBotApi
				.get(`/clients/public_checklists/${checklist_id}`)
				.then((res) => {
					const response = new ChecklistResponse(res);
					console.log(response);
					setDetailItem(response.checklist);
					setLoading(false);
				})
				.catch((err) => {
					const error = new ApiError(err);
					openNotification('error', 'Error', error.errorMessage);
					setLoading(false);
				});
		}
	}, []);

	const handleShowDetail = async (item) => {
		Router.push(
			{
				pathname: `/resources/checklist-library/${item.title.replace(/[ \/]/g, (char) => {
					const replacement = { '/': '_', ' ': '-' };
					return replacement[char] || char;
				})}`,
				query: { id: item.id },
			},
			undefined,
			{ shallow: true },
		);
	};

	const fetchChecklists = (payload = {}) => {
		setLoading(true);
		facilityBotApi
			.get(`/clients/public_checklists`, {
				params: {
					page: checklistsPageInfo.page,
					per_page: checklistsPageInfo.per_page,
					...filter,
					...payload,
				},
			})
			.then((res) => {
				const response = new ChecklistsResponse(res);
				setChecklists(response.checklists);
				setchecklistsPageInfo(response.pagination);
				setLoading(false);
			})
			.catch((err) => {
				const error = new ApiError(err);
				openNotification('error', 'Error', error.errorMessage);
				setLoading(false);
			});
	};

	const handlePageChange = async (page) => {
		fetchChecklists({ page: page });
	};

	const handleCategoryChange = async (category_id) => {
		handleShowList();
		setFilter((prevFilter) => {
			return {
				...prevFilter,
				category_id: category_id,
			};
		});

		fetchChecklists({
			page: 1,
			per_page: 10,
			category_id: category_id,
		});
	};

	const onSearch = (value) => {
		setFilter((prevFilter) => {
			return {
				...prevFilter,
				q: value,
			};
		});
		fetchChecklists({ q: value });
	};

	const setSortType = (type) => {
		setFilter((prevFilter) => {
			return {
				...prevFilter,
				data_sort: type,
			};
		});
		fetchChecklists({ data_sort: type });
	};

	const handleShowList = () => {
		setShowDetail(false);
		setDetailItem({});
		Router.push({ query: undefined }, undefined, { shallow: true });
	};

	const handleDownloadPdf = async (item) => {
		try {
			await facilityBotApi.get(`/clients/public_checklists/${item.id}/download_count`);
		} catch (err) {
			console.error('🚀 ~ file: index.js ~ line 98 ~ handleDownloadPdf ~ err', err);
		}
	};

	useEffect(() => {
		if (typeof window === 'undefined') return;
		const handleScroll = () => {
			const scrollPos = window.scrollY + 220;
			for (let i = CHECKLIST_DEPARTMENTS.length - 1; i >= 0; i--) {
				const el = document.getElementById(CHECKLIST_DEPARTMENTS[i].id);
				if (el && scrollPos >= el.offsetTop) {
					setActiveSection(CHECKLIST_DEPARTMENTS[i].id);
					break;
				}
			}
		};

		window.addEventListener('scroll', handleScroll, { passive: true });
		return () => window.removeEventListener('scroll', handleScroll);
	}, []);

	const toggleDeptExpand = (deptId) => {
		setExpandedDepts((prev) => ({
			...prev,
			[deptId]: !prev[deptId],
		}));
	};

	const toggleFaq = (index) => {
		setOpenFaqIndex((prev) => (prev === index ? null : index));
	};

	const handleJumpToSection = (e, id) => {
		e.preventDefault();
		setActiveSection(id);
		const element = document.getElementById(id);
		if (element) {
			element.scrollIntoView({ behavior: 'smooth', block: 'start' });
			if (typeof window !== 'undefined' && window.history.replaceState) {
				window.history.replaceState(null, '', `#${id}`);
			}
		}
	};

	const handleDepartmentCta = (categoryName) => {
		handleShowList();
		const matchedCategory = categories.find(
			(c) => c.title.trim().toLowerCase() === categoryName.trim().toLowerCase(),
		);
		const categoryId = matchedCategory ? matchedCategory.id : null;

		setFilter((prevFilter) => ({
			...prevFilter,
			category_id: categoryId,
		}));

		fetchChecklists({
			page: 1,
			per_page: 10,
			category_id: categoryId,
		});

		setTimeout(() => {
			const toolEl = document.getElementById('checklist-library-tool');
			if (toolEl) {
				toolEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
			}
		}, 150);
	};

	const faqSchema = {
		'@context': 'https://schema.org',
		'@type': 'FAQPage',
		mainEntity: CHECKLIST_FAQS.map((faq) => ({
			'@type': 'Question',
			name: faq.question,
			acceptedAnswer: {
				'@type': 'Answer',
				text: faq.answer,
			},
		})),
	};

	return (
		<Layout pageName="Checklist Library">
			<Head>
				<script
					type="application/ld+json"
					dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
				/>
			</Head>
			<div className="resource-page resource-page--checklist-library">
				<section className="heading-container">
					<div className="container">
						<div className="heading">
							<h1 className="heading__title">Checklist Library</h1>

							<h2 className="heading__desc heading__desc--mb-1">Get started quickly with a checklist template</h2>

							<h2 className="heading__desc heading__desc--mb-1">
								If you cannot find the checklist you are looking for, try our new{' '}
								<a href="checklist-generator">ChatGPT-powered checklist generator</a>
							</h2>

							<h2 className="heading__desc heading__desc--mb-1">
								We welcome checklist contributions. If you have a checklist that you would like to contribute to our
								checklist library, please send it to us at{' '}
								<a href="mailto:sales@facilitybot.co">sales@facilitybot.co</a>
							</h2>
						</div>
					</div>
				</section>

				{/* Sticky Jump-Nav Bar */}
				<nav className="checklist-jump-nav" aria-label="Department Checklists Jump Navigation">
					<div className="container">
						<div className="checklist-jump-nav__track">
							{CHECKLIST_DEPARTMENTS.map((dept) => (
								<a
									key={dept.id}
									href={`#${dept.id}`}
									className={`checklist-jump-nav__pill ${activeSection === dept.id ? 'active' : ''}`}
									onClick={(e) => handleJumpToSection(e, dept.id)}
								>
									{dept.navTitle}
								</a>
							))}
						</div>
					</div>
				</nav>

				{/* Content Block from Document */}
				<div className="container checklist-content-block">
					{/* Lead Overview */}
					<section className="checklist-lead">
						<h2 className="checklist-lead__title">{CHECKLIST_OVERVIEW.title}</h2>
						{CHECKLIST_OVERVIEW.intro.map((p, idx) => (
							<p key={idx} className="checklist-lead__p">
								{p}
							</p>
						))}
					</section>

					{/* What is PM Checklist Card */}
					<div className="checklist-info-card">
						<h3 className="checklist-info-card__title">{CHECKLIST_OVERVIEW.whatIs.title}</h3>
						<p className="checklist-info-card__desc">{CHECKLIST_OVERVIEW.whatIs.description}</p>
						<h4 className="checklist-info-card__subtitle">{CHECKLIST_OVERVIEW.whatIs.frameworkTitle}</h4>
						<div className="checklist-framework-grid">
							{CHECKLIST_OVERVIEW.whatIs.frameworkItems.map((item, idx) => (
								<div key={idx} className="checklist-framework-grid__item">
									<span className="item-label">{item.label}</span>
									<span className="item-desc">{item.desc}</span>
								</div>
							))}
						</div>
					</div>

					{/* Why PM Checklists Matter */}
					<div className="checklist-info-card">
						<h3 className="checklist-info-card__title">{CHECKLIST_OVERVIEW.whyMatters.title}</h3>
						<p className="checklist-info-card__desc">{CHECKLIST_OVERVIEW.whyMatters.subtitle}</p>
						<div className="checklist-benefits-grid">
							{CHECKLIST_OVERVIEW.whyMatters.points.map((pt, idx) => (
								<div key={idx} className="checklist-benefits-grid__item">
									<div className="benefit-icon" aria-hidden="true">
										<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
											<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
											<polyline points="22 4 12 14.01 9 11.01" />
										</svg>
									</div>
									<div className="benefit-content">
										<h4>{pt.title}</h4>
										<p>{pt.desc}</p>
									</div>
								</div>
							))}
						</div>
					</div>

					{/* How to Use These Checklists */}
					<div className="checklist-info-card">
						<h3 className="checklist-info-card__title">{CHECKLIST_OVERVIEW.howToUse.title}</h3>
						<p className="checklist-info-card__desc">{CHECKLIST_OVERVIEW.howToUse.subtitle}</p>
						<div className="checklist-howto-grid">
							{CHECKLIST_OVERVIEW.howToUse.steps.map((st, idx) => (
								<div key={idx} className="checklist-howto-grid__item">
									<div className="step-num">{st.step}</div>
									<div className="step-content">
										<h4>{st.title}</h4>
										<p>{st.desc}</p>
									</div>
								</div>
							))}
						</div>
					</div>

					{/* 10 Department Checklist Sections */}
					{CHECKLIST_DEPARTMENTS.map((dept) => {
						const isExpanded = !!expandedDepts[dept.id];
						const totalItems = dept.groups.reduce((acc, grp) => acc + grp.items.length, 0);

						return (
							<section key={dept.id} id={dept.id} className="checklist-dept-section">
								<span className="checklist-dept-section__badge">Checklist Template</span>
								<h2 className="checklist-dept-section__title">{dept.heading}</h2>
								<p className="checklist-dept-section__intro">{dept.intro}</p>

								{/* Checklist items container with mobile collapse/expand */}
								<div className={`checklist-items-container ${isExpanded ? 'is-mobile-expanded' : 'is-mobile-collapsed'}`}>
									{dept.groups.map((group, grpIdx) => (
										<div key={grpIdx} className="checklist-group">
											{group.title && (
												<h3 className="checklist-group__title">
													<span>{group.title}:</span>
												</h3>
											)}
											<div className="checklist-items-box">
												<div className="checklist-items-box__grid">
													{group.items.map((item, itemIdx) => (
														<div key={itemIdx} className="checklist-items-box__item">
															<span className="check-icon" aria-hidden="true">
																<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
																	<polyline points="20 6 9 17 4 12" />
																</svg>
															</span>
															<span>{item}</span>
														</div>
													))}
												</div>
											</div>
										</div>
									))}
								</div>

								{/* Mobile Toggle Button */}
								<button
									type="button"
									className={`checklist-mobile-toggle ${isExpanded ? 'open' : ''}`}
									onClick={() => toggleDeptExpand(dept.id)}
									aria-expanded={isExpanded}
								>
									<span>{isExpanded ? 'Hide checklist items' : `View checklist items (${totalItems} items)`}</span>
									<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
										<polyline points="6 9 12 15 18 9" />
									</svg>
								</button>

								{dept.outro && <p className="checklist-dept-section__outro">{dept.outro}</p>}

								<div className="checklist-action-bar">
									<button
										type="button"
										className="btn--download-checklist"
										onClick={() => handleDepartmentCta(dept.categoryName)}
									>
										<DownloadIcon />
										<span>Download checklist</span>
									</button>
									<span className="cta-target-hint">
										Filters library to <strong>{dept.categoryName}</strong> checklists
									</span>
								</div>
							</section>
						);
					})}

					{/* How to Build Your Own Checklist Template */}
					<section className="checklist-guide-block">
						<h2 className="checklist-guide-block__title">{CHECKLIST_BUILD_STEPS.title}</h2>
						<p className="checklist-guide-block__subtitle">{CHECKLIST_BUILD_STEPS.subtitle}</p>
						<div className="checklist-guide-block__grid">
							{CHECKLIST_BUILD_STEPS.steps.map((stepText, idx) => (
								<div key={idx} className="guide-step-card">
									<div className="step-badge">{idx + 1}</div>
									<p className="step-text">{stepText}</p>
								</div>
							))}
						</div>
					</section>

					{/* Frequently Asked Questions */}
					<section className="checklist-faq-section" id="faq-section">
						<h2 className="checklist-faq-section__title">Preventive Maintenance Checklist FAQ</h2>
						<div className="checklist-faq-section__list">
							{CHECKLIST_FAQS.map((faq, idx) => {
								const isOpen = openFaqIndex === idx;
								return (
									<div key={idx} className={`faq-card ${isOpen ? 'open' : ''}`}>
										<div
											className="faq-card__header"
											onClick={() => toggleFaq(idx)}
											role="button"
											tabIndex={0}
											onKeyDown={(e) => {
												if (e.key === 'Enter' || e.key === ' ') {
													e.preventDefault();
													toggleFaq(idx);
												}
											}}
											aria-expanded={isOpen}
										>
											<h3>{faq.question}</h3>
											<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
												<polyline points="6 9 12 15 18 9" />
											</svg>
										</div>
										{isOpen && (
											<div className="faq-card__body">
												<p>{faq.answer}</p>
											</div>
										)}
									</div>
								);
							})}
						</div>
					</section>

					{/* Bottom Conversion CTA */}
					<section className="checklist-bottom-cta">
						<h2 className="checklist-bottom-cta__title">{CHECKLIST_BOTTOM_CTA.title}</h2>
						<p className="checklist-bottom-cta__desc">{CHECKLIST_BOTTOM_CTA.description}</p>
						<div className="checklist-bottom-cta__actions">
							<Link href={CHECKLIST_BOTTOM_CTA.buttons[0].url} className="btn--cta-demo">
								{CHECKLIST_BOTTOM_CTA.buttons[0].text}
							</Link>
							<Link href={CHECKLIST_BOTTOM_CTA.buttons[1].url} className="btn--cta-cmms">
								{CHECKLIST_BOTTOM_CTA.buttons[1].text}
							</Link>
						</div>
					</section>
				</div>

				<section className="container search-bar" id="checklist-library-tool">
					<Search placeholder="Search" allowClear enterButton="Search" size="large" onSearch={onSearch} />
				</section>

				<section className="container checklists">
					<aside className="checklists__sidebar-desktop">
						<button
							className={`checklists__index-btn ${filter.category_id === null && 'checklists__index-btn-primary'}`}
							onClick={(e) => handleCategoryChange(null, e)}
						>
							All Checklists
						</button>
						{categories.map((category, index) => {
							return (
								<button
									className={`checklists__index-btn ${
										filter.category_id === category.id && 'checklists__index-btn-primary'
									}`}
									onClick={(e) => handleCategoryChange(category.id, e)}
									key={index}
								>
									{category.title}
								</button>
							);
						})}
					</aside>

					<aside className="checklists__sidebar-mobile">
						<div className="category-filter-wrapper">
							<div className="category-filter-title">Category:</div>
							<Select
								style={{ width: '100%' }}
								size="large"
								value={filter.category_id === null ? 'all' : filter.category_id}
								onChange={(value) => {
									const categoryId = value === 'all' ? null : value;
									handleCategoryChange(categoryId);
								}}
							>
								<Select.Option value="all">All Checklists</Select.Option>
								{categories.map((category) => (
									<Select.Option value={category.id} key={category.id}>
										{category.title}
									</Select.Option>
								))}
							</Select>
						</div>
					</aside>

					<div className="checklists__content">
						{showDetail ? (
							<DetailItem
								item={detailItem}
								onClickBack={handleShowList}
								onClickDownload={handleDownloadPdf}
								loading={loading}
							/>
						) : (
							<div className="checklists__list">
								<div className="checklists__sorting">
									<div className="checklists__sorting-title">Sort by:</div>

									<span className="checklists__sorting-buttons">
										<Button type={filter.data_sort === 'name' ? 'primary' : null} onClick={() => setSortType('name')}>
											A - Z
										</Button>
										<Button
											type={filter.data_sort === 'created_at' ? 'primary' : null}
											onClick={() => setSortType('created_at')}
										>
											Latest
										</Button>
										<Button
											type={filter.data_sort === 'downloads' ? 'primary' : null}
											onClick={() => setSortType('downloads')}
										>
											Most Downloaded
										</Button>
									</span>
								</div>

								<ChecklistsList
									handleShowDetail={handleShowDetail}
									checklists={checklists}
									pageInfo={checklistsPageInfo}
									onPageChange={handlePageChange}
									loading={loading}
								/>
							</div>
						)}
					</div>
				</section>
			</div>
		</Layout>
	);
}

const ChecklistsList = ({ handleShowDetail, checklists, pageInfo, onPageChange, loading }) => (
	<List
		pagination={{
			showSizeChanger: false,
			onChange: onPageChange,
			total: pageInfo.count,
			current: pageInfo.page,
			showTotal: function (total, range) {
				let [from, to] = range;
				return `Listing ${from}-${to} of ${total}`;
			},
			className: 'checklists-centered-pagination',
		}}
		dataSource={checklists}
		grid={{
			gutter: 16,
			column: 2,
			xs: 1,
			sm: 1,
		}}
		loading={loading}
		itemLayout="horizontal"
		renderItem={(item) => (
			<List.Item>
				<div className="checklists__item" onClick={() => handleShowDetail(item)}>
					<h3 className="checklists__item-title">{item.title}</h3>

					<div className="checklists__item-content">
						<span className="updated-at">Updated: {dayjs.unix(item.updated_at).format('DD/MM/YYYY')}</span>
						<span className="downloads">
							<DownloadIcon /> Downloads: {item.downloads}
						</span>
					</div>
				</div>
			</List.Item>
		)}
	/>
);

const DetailItem = ({ item, onClickBack, onClickDownload, loading }) => (
	<Spin spinning={loading} size="large">
		<div className="detail-checklist">
			<div className="detail-checklist__heading">
				<div className="detail-checklist__title-wrapper">
					<div className="detail-checklist__back-link" onClick={onClickBack}>
						Back to Search Results
					</div>
					<div className="detail-checklist__title">{item.title}</div>
					<div className="detail-checklist__desc">
						<Paragraph ellipsis={{ rows: 3, expandable: true, symbol: 'more' }}>{item.notes}</Paragraph>
					</div>
				</div>

				<div className="detail-checklist__btn-wrapper">
					<SignupForm
						btnText="Sign in & use in facilitybot"
						btnClass="btn btn--checklist"
						type={{ name: 'checklist', id: item.id }}
					/>

					<a
						download={item.title}
						href={`data:application/pdf;base64, ${item.pdf_data}`}
						title="Download pdf document"
						className="btn btn--checklist"
						onClick={() => onClickDownload(item)}
					>
						Download PDF
					</a>
				</div>
			</div>

			{item.pdf_data && (
				<div className="detail-checklist__review-pdf">
					<iframe
						src={`data:application/pdf;base64, ${item.pdf_data}#toolbar=0&navpanes=0&scrollbar=0`}
						className="detail-checklist__review-pdf"
						loading="lazy"
					/>
				</div>
			)}
		</div>
	</Spin>
);

export async function getServerSideProps() {
	try {
		const resChecklists = await facilityBotApi.get('/clients/public_checklists');
		const checklistsResponse = new ChecklistsResponse(resChecklists);

		const resCategories = await facilityBotApi.get('/clients/checklist_categories');
		const categoriesResponse = new ChecklistCategoriesResponse(resCategories);

		return {
			props: {
				initialChecklists: checklistsResponse.checklists,
				initialPageInfo: checklistsResponse.pagination,
				initialCategories: categoriesResponse.categories,
			},
		};
	} catch (err) {
		const error = new ApiError(err);
		console.error('🚀 ~ file: index.js ~ line 318 ~ getServerSideProps ~ err', error.errorMessage);

		return {
			notFound: true,
		};
	}
}
